import {
  ChangeDetectionStrategy,
  Component,
  signal,
  viewChild,
  afterNextRender,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
} from '@angular/core';
import { reviews } from '../data/reviews';
import { ContentContainer } from '../../../core/content-container/content-container';
import { register, SwiperContainer } from 'swiper/element/bundle';
import { TruncateReviewComponent } from './components/truncate-review';
import { NgOptimizedImage } from '@angular/common';

const CAROUSEL_WARNING =
  '[BaseReviews] Não foi possível inicializar o carrossel de avaliações ' +
  '(Shadow Parts / Element.part indisponível). A seção segue estática e a página não é afetada.';

/**
 * Detecta suporte a Shadow Parts (`Element.part`), recurso usado pelo Swiper
 * ao registrar os shadow parts do `<swiper-container>`.
 *
 * Em navegadores onde `Element.part` não existe (ex.: Firefox com Shadow Parts
 * desabilitado), `SwiperContainer.render()` chama `el.part.add('container')` em
 * um `div` recém-criado e lança `TypeError`, derrubando a página inteira.
 */
export function supportsShadowParts(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const probe = document.createElement('div');

  return !!probe.part && typeof probe.part.add === 'function';
}

@Component({
  selector: 'app-base-reviews',
  imports: [ContentContainer, TruncateReviewComponent, NgOptimizedImage],
  templateUrl: './reviews.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BaseReviews {
  reviews = signal(reviews);

  swiper = viewChild.required<ElementRef<SwiperContainer>>('swiper');

  private shadowPartsWarningLogged = false;

  constructor() {
    afterNextRender(() => this.initializeCarousel());
  }

  /**
   * Inicializa o carrossel de avaliações de forma defensiva: quando o ambiente
   * não suporta Shadow Parts, o Swiper falha dentro do próprio `render()` ao
   * acessar `Element.part`. Nesse caso mantemos o conteúdo estático (sem
   * carrossel) em vez de propagar o erro para o `ErrorHandler` do Angular.
   */
  initializeCarousel(): void {
    try {
      register();

      const swiperEl = this.swiper().nativeElement;

      Object.assign(swiperEl, {
        slidesPerView: 1,
        breakpoints: {
          650: {
            slidesPerView: 2,
          },
          980: {
            slidesPerView: 3,
          },
        },
      });

      if (!supportsShadowParts()) {
        this.warnOnce();
        return;
      }

      swiperEl.initialize();
    } catch {
      this.warnOnce();
    }
  }

  private warnOnce(): void {
    if (this.shadowPartsWarningLogged) {
      return;
    }

    this.shadowPartsWarningLogged = true;
    console.warn(CAROUSEL_WARNING);
  }
}
